import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Circle,
  Rectangle,
  DrawingManager,
  Autocomplete,
  Marker,
} from "@react-google-maps/api";
import { Box, Skeleton, Slider } from "@mui/material";
import { Form } from "react-bootstrap";
import PropTypes from "prop-types";
import "./GeofenceCustomStyle.css";

const mapLibraries = ["drawing", "places", "geometry"];

export const DEFAULT_MAP_LAT_LNG = { lat: 37.33479, lng: -121.88814 };

export const GeofenceAddressSearch = ({ map }) => {
  const { isLoaded, error } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_NEXT_PUBLIC_GOOGLE_MAPS_API,
    libraries: mapLibraries,
    version: "3.64",
  });

  const [searchBox, setSearchBox] = useState();

  const onPlaceChanged = () => {
    if (searchBox != null) {
      const place = searchBox.getPlace();
      if (place.geometry?.viewport) {
        map?.fitBounds(place.geometry.viewport);
      } else if (place.geometry?.location) {
        map?.setCenter(place.geometry.location);
        map?.setZoom(15);
      }
    }
  };

  if (!isLoaded || error) {
    return (
      <div className="geofence-map-search-row">
        <input
          type="text"
          placeholder="Search by address"
          className="geofence-map-search-input"
          disabled
        />
        <span className="fa fa-search geofence-map-search-icon" />
      </div>
    );
  }

  return (
    <div className="geofence-map-search-row">
      <Autocomplete
        onPlaceChanged={onPlaceChanged}
        onLoad={(ref) => setSearchBox(ref)}
      >
        <input
          type="text"
          placeholder="Search by address"
          className="geofence-map-search-input"
        />
      </Autocomplete>
      <span className="fa fa-search geofence-map-search-icon" />
    </div>
  );
};

GeofenceAddressSearch.propTypes = {
  map: PropTypes.object,
};

const GeofenceCustomArea = ({
  center,
  setCircle,
  radius,
  setBounds,
  bounds,
  zoom,
  clearClick,
  height,
  setClear,
  fenceType,
  circleValues,
  deviceType,
  hideSearchBar = false,
  onMapReady,
}) => {
  const { isLoaded, error } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_NEXT_PUBLIC_GOOGLE_MAPS_API,
    libraries: mapLibraries,
    version: "3.64",
  });

  const [map, setMap] = useState();
  const [rectangleBounds, setRectangleBounds] = useState(bounds);
  const [circleData, setCircleData] = useState(
    center?.lng != null && center?.lat != null
      ? { center, radius }
      : { center: DEFAULT_MAP_LAT_LNG, radius: 0 },
  );
  const [radiusValue, setRadiusValue] = useState(
    fenceType !== "" ? circleData.radius : 7.62,
  );

  const isValidBounds = (bounds) => {
    return (
      bounds &&
      !isNaN(bounds.north) &&
      !isNaN(bounds.south) &&
      !isNaN(bounds.east) &&
      !isNaN(bounds.west)
    );
  };

  useEffect(() => {
    setCircleData(
      center?.lng != null && center?.lat != null
        ? { center, radius }
        : { center: DEFAULT_MAP_LAT_LNG, radius: 0 },
    );
  }, [center, radius]);

  useEffect(() => {
    if (circleData?.center?.lng != null && circleData?.center?.lat != null) {
      setCircle(circleData);
    } else {
      const defaultCircleData = {
        center: DEFAULT_MAP_LAT_LNG,
        radius: 0,
      };
      setCircle(defaultCircleData);
    }
  }, [circleData]);

  useEffect(() => {
    if (clearClick) {
      setCircleData({ center: undefined, radius: 0 });
      setRectangleBounds(null);
      setClear(0);
    }
  }, [clearClick]);

  useEffect(() => {
    if (isValidBounds(bounds)) {
      setRectangleBounds(bounds);
    } else {
      setRectangleBounds(null);
    }
  }, [bounds]);

  // get user's geo
  useEffect(() => {
    if (center) return;
    if (rectangleBounds) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        map?.setCenter(pos);
        map?.setZoom(zoom);
      },
      () => {
        map?.setCenter(DEFAULT_MAP_LAT_LNG);
        map?.setZoom(2);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 },
    );
  }, [map, zoom]);

  const circleRef = useRef();
  const listenersRef = useRef([]);
  const rectangleRef = useRef();

  const onEditCircle = useCallback(() => {
    if (circleRef.current) {
      let radiusInMeter = circleRef.current.getRadius();
      let _center = circleRef.current.getCenter();
      let radiusInFeet = radiusInMeter * 3.281;

      if (radiusInMeter !== radiusValue) {
        let newRadiusInMeter = radiusInMeter;
        if (radiusInFeet < 25) newRadiusInMeter = 25 / 3.281;
        else if (radiusInFeet > 500) newRadiusInMeter = 500 / 3.281;

        setCircle({
          center: { lat: _center?.lat(), lng: _center?.lng() },
          radius: newRadiusInMeter,
        });
        setRadiusValue(newRadiusInMeter);
      } else {
        setCircle({
          center: { lat: _center?.lat(), lng: _center?.lng() },
          radius: radiusValue,
        });
      }
    }
  }, [setCircle, radiusValue]);

  // Bind refs to current Polygon and listeners
  const onCircleLoad = useCallback(
    (circle) => {
      circleRef.current = circle;
      listenersRef.current.push(
        circle.addListener("set_at", onEditCircle),
        circle.addListener("insert_at", onEditCircle),
        circle.addListener("remove_at", onEditCircle),
      );
      if (center) {
        const map = circleRef.current.getMap();
        map?.fitBounds(circleRef.current.getBounds());
      }
    },
    [onEditCircle, center],
  );

  const onEditRectangle = useCallback(() => {
    if (rectangleRef.current) {
      const bounds = rectangleRef.current.getBounds();
      const NE = bounds.getNorthEast();
      const SW = bounds.getSouthWest();

      const updatedBounds = {
        north: NE.lat(),
        south: SW.lat(),
        east: NE.lng(),
        west: SW.lng(),
      };

      if (
        updatedBounds.north !== rectangleBounds.north ||
        updatedBounds.south !== rectangleBounds.south ||
        updatedBounds.east !== rectangleBounds.east ||
        updatedBounds.west !== rectangleBounds.west
      ) {
        setBounds(updatedBounds);
      }
    }
  }, [setBounds, rectangleBounds]);

  const onRectangleLoad = useCallback((rectangle) => {
    rectangleRef.current = rectangle;
  }, []);

  // Clean up refs
  const onCircleUnmount = useCallback(() => {
    listenersRef.current.forEach((lis) => lis.remove());
    circleRef.current = undefined;
  }, []);

  const onRectangleUnmount = useCallback(() => {
    rectangleRef.current = undefined;
  }, []);

  const [searchBox, setSearchBox] = useState();

  const searchBoxOnload = (ref) => {
    setSearchBox(ref);
  };

  const onPlaceChanged = () => {
    if (searchBox != null) {
      const place = searchBox.getPlace();
      if (place.geometry?.viewport) {
        map?.fitBounds(place.geometry.viewport);
      } else if (place.geometry?.location) {
        map?.setCenter(place.geometry.location);
        map?.setZoom(15);
      }
    }
  };

  const handleMapLoad = useCallback(
    (mapInstance) => {
      setMap(mapInstance);
      onMapReady?.(mapInstance);
    },
    [onMapReady],
  );

  if (!isLoaded || error) {
    return (
      <Skeleton
        variant="rectangular"
        animation="wave"
        height={typeof height === "string" ? parseInt(height, 10) || 280 : height || 280}
        className="geofence-map-skeleton"
      />
    );
  }

  const handleChange = (fieldName, val) => {
    let radius = val / 3.281;
    setRadiusValue(radius);
  };

  const handleInput = (e) => {
    let radius = Number(e.target.value) / 3.281;
    setRadiusValue(radius);
  };

  return (
    <Box className="geofence-map-container">
      {!hideSearchBar && (
        <div className="geofence-map-search-row">
          <Autocomplete onPlaceChanged={onPlaceChanged} onLoad={searchBoxOnload}>
            <input
              type="text"
              placeholder="Search by address"
              className="geofence-map-search-input"
            />
          </Autocomplete>
          <span className="fa fa-search geofence-map-search-icon" />
        </div>
      )}

      <div className="geofence-map-canvas">
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: height }}
          zoom={fenceType !== "RECTANGLE" ? zoom : 17}
          center={
            fenceType !== "RECTANGLE"
              ? center
              : rectangleBounds !== null
                ? { lat: rectangleBounds.north, lng: rectangleBounds.east }
                : ""
          }
          onLoad={handleMapLoad}
          options={{
            streetViewControl: false,
            fullscreenControlOptions: {
              position: window.google.maps.ControlPosition.RIGHT_BOTTOM,
            },
          }}
        >
          {circleData?.center && circleData?.radius && (
            <>
              <Circle
                editable={true}
                draggable={true}
                center={circleValues.center}
                radius={radiusValue}
                onDragEnd={onEditCircle}
                onRadiusChanged={onEditCircle}
                onLoad={onCircleLoad}
                onUnmount={onCircleUnmount}
                options={{
                  fillColor: `#009eee`,
                  strokeColor: "#009eee",
                  strokeWeight: 1,
                }}
              />

              <Marker position={circleValues.center} />
            </>
          )}
          {rectangleBounds && (
            <>
              <Rectangle
                bounds={rectangleBounds}
                editable
                draggable
                onBoundsChanged={onEditRectangle}
                onLoad={onRectangleLoad}
                onUnmount={onRectangleUnmount}
                options={{
                  fillColor: `#009eee`,
                  strokeColor: "#009eee",
                  strokeWeight: 1,
                }}
              />
              <Marker
                position={{
                  lat: (rectangleBounds.north + rectangleBounds.south) / 2,
                  lng: (rectangleBounds.east + rectangleBounds.west) / 2,
                }}
              />
            </>
          )}
          {(!circleData.center || !circleData.radius) && !rectangleBounds && (
            <div style={{ width: "200px" }}>
              <MyDrawingManager
                setCircle={setCircleData}
                setBounds={setBounds}
                setRadius={setRadiusValue}
                deviceType={deviceType}
              />
            </div>
          )}
        </GoogleMap>
      </div>

      {!rectangleBounds && (
        <div className="geofence-radius-controls">
          <div className="geofence-radius-row">
            <span className="geofence-radius-label">Radius</span>
            <span className="geofence-radius-slider">
              <Slider
                min={25}
                max={500}
                value={Math.round(radiusValue * 3.281)}
                onChange={handleChange}
              />
            </span>
            <div className="geofence-radius-input-wrap">
              <Form.Control
                min={25}
                max={500}
                type={"number"}
                name={"radius"}
                value={Math.round(radiusValue * 3.281)}
                onChange={handleInput}
              />
              <span className="geofence-radius-unit">ft</span>
            </div>
          </div>
          <p className="geofence-radius-helper">
            Define how far from the pinned location counts as “inside” or
            “outside” this location
          </p>
        </div>
      )}
    </Box>
  );
};

const MyDrawingManager = ({ setCircle, setBounds, setRadius, deviceType }) => {
  const onCircleComplete = (circle) => {
    let radiusInMeter = circle.getRadius();
    let radiusInFeet = radiusInMeter * 3.281;
    let newRadiusInMeter = radiusInMeter;
    if (radiusInFeet < 25) newRadiusInMeter = 25 / 3.281;
    else if (radiusInFeet >= 500) newRadiusInMeter = 500 / 3.281;

    setCircle({
      center: {
        lat: circle.getCenter()?.lat(),
        lng: circle.getCenter()?.lng(),
      },
      radius: newRadiusInMeter,
    });
    circle.setMap(null);

    setRadius(newRadiusInMeter);
  };

  const onRectangleComplete = (rectangle) => {
    const bounds = rectangle.getBounds();
    const NE = bounds.getNorthEast();
    const SW = bounds.getSouthWest();

    const newBounds = {
      north: NE.lat(),
      south: SW.lat(),
      east: NE.lng(),
      west: SW.lng(),
    };

    setBounds(newBounds);
    rectangle.setMap(null);
  };

  const drawingModes = [
    window.google.maps.drawing.OverlayType.CIRCLE,
    ...(deviceType !== 2
      ? [window.google.maps.drawing.OverlayType.RECTANGLE]
      : []),
  ];

  return (
    <DrawingManager
      options={{
        drawingControl: true,
        drawingControlOptions: {
          drawingModes,
        },
      }}
      onCircleComplete={onCircleComplete}
      onRectangleComplete={onRectangleComplete}
    />
  );
};

MyDrawingManager.propTypes = {
  setCircle: PropTypes.func,
  setBounds: PropTypes.func.isRequired,
  setRadius: PropTypes.func,
  deviceType: PropTypes.number,
};

GeofenceCustomArea.propTypes = {
  zoom: PropTypes.number,
  clearClick: PropTypes.number,
  center: PropTypes.object,
  radius: PropTypes.number,
  setCircle: PropTypes.func,
  setBounds: PropTypes.func.isRequired,
  hideSearchBar: PropTypes.bool,
  onMapReady: PropTypes.func,
};

export default GeofenceCustomArea;
